import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../BaseElement";

import { styles } from "./MediaEditor.style";
import { IPlaceMediaItem } from "../../../main";
import { groupAccessToProtectionLevel, ProtectionLevel, protectionLevelToGroups } from "../../User";
import { FormsT } from "../form/FormTypes";
import { MediaFields } from "./Media.form";
import { litIf } from "../LitUtils";
import { cloneDeep } from "lodash-es";
import { watch } from "../internal/watch";
import { isWritable } from "../../Utils";
import { showConfirmDialog } from "../ConfirmDialog";
import { IPlaceTable } from "../../backend/IPlaceTable";

@customElement("ds-media-editor")
export class MediaEditor extends BaseElement {
    static override styles = [styles];
    @property({ type: Object }) placeMedia?: IPlaceMediaItem;

    @state() protected _saving = false;
    @state() protected _modified = false;

    private _placeTable = this._kernel.dataStore.getSource<IPlaceTable>("places");

    @watch("placeMedia")
    _placeMediaUpdated() {
        if (this.placeMedia) {
            if (!isWritable(this.placeMedia?.media, "name")) {
                console.log("Not writable");
            }
        }
    }

    private _raiseModifiedEvent() {
        this._raiseEvent("ds-modified", { media: cloneDeep(this.placeMedia) });
        this._modified = true;
    }

    private _deleteMediaWithConfirmation(id: string) {
        showConfirmDialog(
            {
                open: true,
                prompt: "Delete media?",
                message:
                    "This will irretrievably delete the media. Press 'Yes' to delete or 'Cancel' to abort.",
            },
            async () => {
                this._saving = true;
                await this._placeTable.deleteMediaItem(id);
                this._saving = false;
                this.placeMedia = undefined;
                this._raiseEvent("ds-deleted", { id });
            }
        );
    }

    private _attributeToCurrentUser() {
        const user = this._kernel.user;
        if (user && this.placeMedia) {
            Object.assign(this.placeMedia.media, {
                attribution: undefined,
                contributorId: user.id,
            });
            this.placeMedia = cloneDeep(this.placeMedia);
            this._raiseModifiedEvent();
        }
    }

    private _renderFooter() {
        return html`<div class="editor-footer" slot="footer">
                <div class="flex-row" style="margin-top:0.5em;">
                    <sl-button
                        variant="default"
                        @click=${() => {
                            if (this.placeMedia) {
                                this._deleteMediaWithConfirmation(this.placeMedia.id);
                            }
                        }}
                        >Delete</sl-button
                    >
                    <sl-button
                        style="margin-left:auto"
                        variant="default"
                        @click="${() => {
                            this._raiseEvent("ds-close");
                        }}"
                        >${this._modified ? "Discard changes" : "Cancel"}</sl-button
                    >
                    <sl-button
                        variant="success"
                        ?disabled=${!this._modified}
                        @click=${async () => {
                            if (this.placeMedia) {
                                if (this.placeMedia.id.length > 0) {
                                    this._saving = true;
                                    await this._placeTable.updateMediaItem(this.placeMedia);
                                    this._saving = false;
                                    this._raiseEvent("ds-saved", {
                                        media: cloneDeep(this.placeMedia),
                                    });
                                }
                            }

                            this._modified = false;
                        }}
                        >Save</sl-button
                    >
                </div>
            </div>
        </div>`;
    }

    override render() {
        return litIf(
            this.placeMedia,
            () =>
                html` ${litIf(
                        this._saving,
                        () =>
                            html`<sl-dialog open no-header label="Saving media...">
                                <ds-progress-spinner></ds-progress-spinner
                            ></sl-dialog>`
                    )}

                    <ds-fixed-panel
                        ><div class="flex-row editor-media-container" slot="body">
                            <div class="flex-column">
                                <ds-form
                                    .object=${this.placeMedia}
                                    .fields=${[
                                        {
                                            type: "enumSelect",
                                            title: "Privacy",
                                            property: "groupAccess",
                                            enumType: ProtectionLevel,
                                            helpMsg: "Who can see this media item",
                                            toEnum: (x: string[]) =>
                                                groupAccessToProtectionLevel(x),
                                            fromEnum: (x: ProtectionLevel) =>
                                                protectionLevelToGroups(x),
                                        },
                                    ] as FormsT[]}
                                    @ds-form-changed=${(e: CustomEvent) => {
                                        if (this.placeMedia) {
                                            this.placeMedia.groupAccess = [
                                                ...e.detail.object.groupAccess,
                                            ];
                                            this._raiseModifiedEvent();
                                        }
                                    }}
                                >
                                </ds-form>
                                <ds-form
                                    .object=${this.placeMedia?.media ?? {}}
                                    .fields=${MediaFields}
                                    @ds-form-changed=${(e: CustomEvent) => {
                                        if (this.placeMedia) {
                                            this.placeMedia.media = cloneDeep(e.detail.object);
                                            this._raiseModifiedEvent();
                                        }
                                    }}
                                ></ds-form>
                                <sl-button
                                    pill
                                    variant="success"
                                    @click=${() => this._attributeToCurrentUser()}
                                    >Attribute to me</sl-button
                                >
                            </div>
                            <ds-media
                                hideCloseButton
                                slot="image"
                                .media=${(this.placeMedia as IPlaceMediaItem)?.media ?? {}}
                            ></ds-media>
                        </div>

                        ${this._renderFooter()}
                    </ds-fixed-panel>`
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-media-editor": MediaEditor;
    }
}
